import type { ButtonProps as SystemButtonProps } from '@hover/blueprint';
import { Button as SystemButton, forwardRef } from '@hover/blueprint';
import React, { useEffect, useState } from 'react';

import { useEffectOnMount, useTracking } from 'src/shared/hooks';
import type {
  OptionalTrackingProperties,
  UseTrackingProps,
} from 'src/shared/hooks/useTracking';
import { buttonImpression } from 'src/shared/telemetry';

export interface ButtonProps
  extends SystemButtonProps,
    Omit<UseTrackingProps, 'buttonText'> {
  includeTrackingImpression?: boolean;
  optionalImpressionProperties?: OptionalTrackingProperties;
}

const Button = forwardRef<ButtonProps, 'button'>(
  (
    {
      pageName,
      primaryCallToAction = false,
      buttonLocation,
      children,
      onClick,
      label,
      track,
      includeTrackingImpression = false,
      type,
      optionalTrackingProperties = {},
      optionalImpressionProperties = {},
      ...props
    },
    ref,
  ) => {
    const buttonText = typeof children === 'string' ? children : label;

    // Disable form submission until client-side hydration
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const trackingData = {
      buttonLocation,
      buttonText,
      optionalTrackingProperties,
      pageName,
      primaryCallToAction,
      track,
    };

    useEffect(() => setIsSubmitDisabled(false), []);

    const { onClick: onClickWithTracking } = useTracking(trackingData);

    useEffectOnMount(() => {
      if (includeTrackingImpression) {
        buttonImpression({
          primary_cta: primaryCallToAction,
          button_text: buttonText || '',
          page_or_screen_name: pageName || '',
          button_location:
            buttonLocation || optionalTrackingProperties?.link_location,
          ...optionalTrackingProperties,
          ...optionalImpressionProperties,
        });
      }
    });

    return (
      <SystemButton
        {...props}
        label={label}
        onClick={event => {
          onClick?.(event);
          onClickWithTracking(event);
        }}
        ref={ref}
        type={isSubmitDisabled && type === 'submit' ? 'button' : type}
      >
        {children}
      </SystemButton>
    );
  },
);

export { Button };
